import { MouseEvent } from "react";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import { PricebookPeriodOverviewRow } from "../../../types/price-book.type";
import { formatNumber } from "../../../utils";
import NameWithCode from "../shared/NameWithCode";
import { CATALOGUE_CELL_PADDING, STRIPE_BACKGROUND } from "../shared/constants";
import PeriodRangeText from "../PriceBook/Periods/PeriodRangeText";
import PeriodStatusChip from "../PriceBook/Periods/PeriodStatusChip";
import {
  NEXT_PRICE_SOURCE_LABEL,
  isPeriodEnded,
} from "../PriceBook/Periods/periodModel";

const NO_VALUE = "-";

/** "รวม VAT / ก่อน VAT", or "-" for a line without a unit price of its own. */
const formatPricePair = (
  priceInclVat: number | null,
  priceExVat: number | null,
): string =>
  priceInclVat === null || priceExVat === null
    ? NO_VALUE
    : `${formatNumber(priceInclVat)} / ${formatNumber(priceExVat)}`;

interface PricePeriodRowProps {
  row: PricebookPeriodOverviewRow;
  striped: boolean;
  /** Shows the extend button (a catalogue writer). */
  canExtend: boolean;
  onOpen: (row: PricebookPeriodOverviewRow) => void;
  onExtend: (row: PricebookPeriodOverviewRow) => void;
}

/** One period of the overview; the row opens its book on the ช่วงราคา tab. */
export default function PricePeriodRow({
  row,
  striped,
  canExtend,
  onOpen,
  onExtend,
}: PricePeriodRowProps) {
  const isEnded = isPeriodEnded(row.status);
  const nextPriceText = formatPricePair(
    row.next_price.price_incl_vat,
    row.next_price.price_ex_vat,
  );

  const handleOpen = () => onOpen(row);
  const handleExtend = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onExtend(row);
  };

  return (
    <TableRow
      hover
      onClick={handleOpen}
      sx={{
        bgcolor: (theme) =>
          striped ? STRIPE_BACKGROUND : theme.palette.background.paper,
        "& td": { padding: `${CATALOGUE_CELL_PADDING}px` },
        cursor: "pointer",
      }}
    >
      <TableCell>
        <NameWithCode name={row.pricebook_name} code={row.pricebook_code} />
      </TableCell>
      <TableCell>
        <NameWithCode name={row.product_name} code={row.product_code} />
      </TableCell>
      <TableCell align="center">
        <PeriodRangeText period={row} />
      </TableCell>
      <TableCell align="center">{formatNumber(row.price_incl_vat)}</TableCell>
      <TableCell align="center">{formatNumber(row.price_ex_vat)}</TableCell>
      <TableCell align="center">
        <Typography variant="body2">{nextPriceText}</Typography>
        <Typography variant="caption" color="text.secondary">
          {NEXT_PRICE_SOURCE_LABEL[row.next_price.source]}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <PeriodStatusChip status={row.status} />
      </TableCell>
      {canExtend && (
        <TableCell align="center">
          {!isEnded && (
            <Tooltip title="ต่อช่วงราคา">
              <IconButton
                size="small"
                color="primary"
                aria-label={`ต่อช่วงราคา ${row.pricebook_code} ${row.product_code}`}
                onClick={handleExtend}
              >
                <MoreTimeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
